import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

const AboutPage = () => {
  const experienceRefs = useRef([])

  useEffect(() => {
    // Scroll to top when component mounts with smooth behavior
    // Account for navbar height if needed
    const navbarHeight = document.querySelector('.navbar')?.offsetHeight || 0
    
    // Small delay to ensure component is fully mounted
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }, 100)
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const numberElement = entry.target.querySelector('.experience-number')
          const finalValue = numberElement.textContent
          animateNumber(numberElement, finalValue)
          observer.unobserve(entry.target)
        }
      })
    })

    experienceRefs.current.forEach(ref => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [])

  const animateNumber = (element, finalValue) => {
    const duration = 2000
    const startTime = performance.now()
    const startValue = 0
    const endValue = parseInt(finalValue.replace(/\D/g, ''))
    
    function updateNumber(currentTime) {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Easing function for smoother animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart)
      element.textContent = currentValue + (finalValue.includes('+') ? '+' : '')
      
      if (progress < 1) {
        requestAnimationFrame(updateNumber)
      }
    }
    
    requestAnimationFrame(updateNumber)
  }

  return (
    <div className="about-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">درباره من</h1>
          <p className="page-subtitle">معلم ریاضی با بیش از ۲۰ سال تجربه</p>
        </div>

        <div className="about-content">
          <div className="about-text">
            <h3>سفر آموزشی من</h3>
            <p>
              بیش از ۲۰ سال سابقه تدریس در مدارس ، آموزشگاه و کلاس های خصوصی حضوری و آنلاین. در طول این سال ها صد ها دانش‌آموز را در سطوح مختلف آموزش داده ام و بزرگ ترین هدفم یادگیری ریاضی با عشق و لذت بوده است.
            </p>
            <p>
              بسیاری از دانش‌آموزان در این مسیر با  ریاضی آشتی کرده و به آن علاقه مند شده‌اند.
            </p>
            <p>
              فلسفه آموزشی من بر پایه درک عمیق مفاهیم، حل مسئله خلاقانه و ایجاد اعتماد به نفس در دانش‌آموزان استوار است. معتقدم که هر دانش‌آموزی قابلیت یادگیری ریاضی را دارد.
            </p>
            <h3>درباره من</h3>
            <p>
              تحصیلات دبیرستانی ام را در مدارس دولتی و تیزهوشان گذرانده ، فارغ التحصیل مدرک کاردانی ریاضی انفورماتیک از دانشگاه علم و صنعت بهشر در سال ۸۳ و مدرک کارشناسی حسابداری از دانشگاه حکیم جرجانی در سال ۹۱ هستم.
            </p>
            <h3>روش‌های تدریس من</h3>
            <p>
              در کلاس‌های من ، دانش‌آموزان نه تنها فرمول‌ها را یاد می‌گیرند ، بلکه درک عمیقی از مفاهیم ریاضی پیدا می‌کنند. من از روش‌های مختلفی مانند حل مسئله و کار گروهی استفاده می‌کنم.
            </p>
            
            <h3>تخصص‌های من</h3>
            <ul>
              <li>ریاضی پنجم و ششم ابتدایی</li>
              <li>ریاضی هفتم ، هشتم ، نهم متوسطه اول</li>
              <li>ریاضی دهم رشته تجربی ، ریاضی ، انسانی و فنی</li>
              <li>ریاضی یازدهم رشته تجربی ، انسانی و فنی</li>
              <li>ریاضی دوازدهم رشته تجربی ، انسانی و فنی</li>
              <li>علوم هفتم ، هشتم ، نهم متوسطه اول</li>
              <li>عربی هفتم ، هشتم ، نهم متوسطه اول</li>
            </ul>
            
            <h3>دستاوردهای من</h3>
            <p>
              در طول سال‌های تدریس، بسیاری از دانش‌آموزان من در کنکور سراسری موفق شده‌اند و در دانشگاه‌های معتبر پذیرفته شده‌اند.
            </p>
          </div>
          
          <div className="about-image">
            <div className="about-placeholder">
              <img 
                src="/images/teacher2.webp" 
                alt="سمیه ارباب مجنی در حال تدریس" 
                className="about-profile-image" 
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="experience-section">
          <h3>آمار و تجربیات</h3>
          <div className="experience-grid">
            <div 
              ref={el => experienceRefs.current[0] = el}
              className="experience-item card-base card-light-sweep card-overlay"
            >
              <div className="experience-number">+20</div>
              <div className="experience-label">سال تجربه</div>
            </div>
            <div 
              ref={el => experienceRefs.current[1] = el}
              className="experience-item card-base card-light-sweep card-overlay"
            >
              <div className="experience-number">+500</div>
              <div className="experience-label">دانش‌آموز موفق</div>
            </div>
            <div 
              ref={el => experienceRefs.current[2] = el}
              className="experience-item card-base card-light-sweep card-overlay"
            >
              <div className="experience-number">+10</div>
              <div className="experience-label">حوزه تخصصی</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AboutPage 